import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import clsx from 'clsx';
import { isAddress } from 'viem';

import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import BetButton from 'shared/components/ui/bet-button/bet-button';
import { useAccount } from 'shared/hooks/use-web3';

import ClipboardIcon from 'assets/clipboard.svg?react';

import { renderHearts, SelectableButton } from './additional-settings-modal-content.utils';
import { GameLength, gameLengths, RoomType, roomTypes } from './constants';

interface IAdditionalSettingsModalContentProps {
  isMobile?: boolean;
  onClose: () => void;
}

export const AdditionalSettingsModalContent = ({
  isMobile,
  onClose,
}: IAdditionalSettingsModalContentProps) => {
  const { t } = useTranslation();
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType>(RoomType.Open);
  const [selectedLength, setSelectedLength] = useState<GameLength>(GameLength.Short);
  const [inviteInputValue, setInviteInputValue] = useState('');
  const [inviteError, setInviteError] = useState('');
  const { address: currentUserAddress } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const onInviteChange = (value: string) => {
    const trimmed = value.trim();
    setInviteInputValue(trimmed);

    if (!trimmed) {
      setInviteError('');
      return;
    }

    if (!isAddress(trimmed)) {
      setInviteError(t('modals.additionalSettings.errors.invalidAddress'));
      return;
    }

    if (currentUserAddress && trimmed.toLowerCase() === currentUserAddress.toLowerCase()) {
      setInviteError(t('modals.additionalSettings.errors.inviteYourself'));
      return;
    }

    setInviteError('');
  };
  useEffect(() => {
    const inviteeAddress = searchParams.get('invite');
    const wins = searchParams.get('wins');
    if (wins) {
      setSelectedLength(wins as GameLength);
    }

    if (inviteeAddress && isAddress(inviteeAddress)) {
      setInviteInputValue(inviteeAddress);
      setInviteError('');
      setSelectedRoomType(RoomType.Private);
    }
  }, [searchParams]);

  const handleApply = () => {
    const next = new URLSearchParams(searchParams);
    next.set('wins', selectedLength);
    next.set('invite', inviteInputValue);
    if (selectedRoomType === RoomType.Open) {
      next.delete('invite');
    }
    setSearchParams(next, { replace: false });
    onClose();
  };

  return (
    <div className="overflow-y-auto">
      <div className="mb-4 flex flex-col border-b border-indigo pb-4 lg:mb-6 lg:pb-6">
        <p className="mb-3 text-[14px] font-bold text-white lg:mb-4 lg:text-[21px]">
          {t('modals.additionalSettings.roomType')}
        </p>
        <div className="mb-4 flex gap-2 rounded-xl border border-indigo bg-[#05055B] p-1 lg:mb-6 lg:rounded-[18px] lg:border-2 lg:p-2">
          {roomTypes.map((type) => (
            <SelectableButton
              key={type}
              label={type}
              isSelected={selectedRoomType === type}
              onClick={() => setSelectedRoomType(type)}
            />
          ))}
        </div>
        <p className="text-sm font-normal text-light-grape lg:text-[15px]">
          {selectedRoomType === RoomType.Open
            ? t('modals.additionalSettings.openRoomDescription')
            : t('modals.additionalSettings.privateRoomDescription')}
        </p>
      </div>

      <div className="mb-4 flex flex-col border-b border-indigo pb-4 lg:mb-6 lg:pb-6">
        <p className="mb-3 text-[14px] font-bold text-white lg:mb-4 lg:text-[21px]">
          {t('modals.additionalSettings.gameLength')}
        </p>
        <div className="mb-4 flex gap-2 rounded-xl border border-indigo bg-[#05055B] p-1 lg:mb-6 lg:rounded-[18px] lg:border-2 lg:p-2">
          {gameLengths.map((length) => (
            <SelectableButton
              key={length}
              label={length}
              isSelected={selectedLength === length}
              onClick={() => setSelectedLength(length)}
            />
          ))}
        </div>

        <div className="flex items-center justify-center gap-2">
          <p className="flex text-[10px] font-normal leading-[14px] text-light-grape lg:text-[15px] lg:leading-5">
            {t(`gameLength.${selectedLength}`)}
          </p>
          <div className="flex min-h-[28px] min-w-[30%] flex-1 shrink-0 items-center justify-center gap-2 rounded-lg border border-indigo bg-[#131353] px-[9px] lg:min-h-[42px] lg:min-w-[32%] lg:rounded-xl">
            {renderHearts(selectedLength)}
          </div>
        </div>
      </div>

      <div className={clsx('flex flex-col', isMobile && 'mb-6')}>
        <p className="mb-1 text-[14px] font-bold text-white lg:mb-4 lg:text-[21px]">
          {t('modals.additionalSettings.invitePlayer')}
        </p>
        <p className="mb-4 text-[10px] font-normal text-white lg:mb-6 lg:text-[15px]">
          {selectedRoomType === RoomType.Private ? (
            <span>{t('modals.additionalSettings.pastePlayerBAddress')}</span>
          ) : (
            <span className="text-yellow">{t('modals.additionalSettings.makeRoomPrivate')}</span>
          )}
        </p>
        <div className="relative w-full">
          <ClipboardIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-light-grape lg:h-7 lg:w-7" />
          <input
            disabled={selectedRoomType === RoomType.Open}
            placeholder={t('modals.additionalSettings.inputAddressPlaceholder')}
            className={clsx(
              'h-[38px] w-full rounded-xl border-2 bg-[#191750] py-[10px] pl-[35px] pr-[18px] text-md font-medium text-white placeholder-light-grape outline-none disabled:opacity-50 lg:h-[57px] lg:pl-[45px] lg:text-[18px]',
              inviteError ? 'border-[#ED5264]' : 'border-indigo',
            )}
            value={inviteInputValue}
            onChange={(e) => onInviteChange(e.target.value)}
          />
        </div>
        {inviteError && <p className="mt-1 text-md text-[#ED5264]">{inviteError}</p>}
        <div className="mt-6 flex justify-between">
          <BetButton className="w-[45%]" text={t('common.cancel')} onClick={onClose} />
          <PrimaryButton
            className="w-[45%]"
            text="common.apply"
            disabled={
              inviteError !== '' ||
              (selectedRoomType === RoomType.Private && inviteInputValue.trim() === '')
            }
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};
